import React from 'react';

/**
 * Reusable table component for displaying player stats and z-scores
 *
 * @param {Array} players - Array of player objects with stats and z_scores
 * @param {string} teamName - Optional team name to display as header
 * @param {boolean} showTeamTotals - Whether to show team summary row (default: false)
 * @param {boolean} showStats - Whether to show raw stats columns (default: true)
 * @param {Object} customStyles - Optional custom styling
 */
const PlayerStatsTable = ({
  players,
  teamName = null,
  showTeamTotals = false,
  showStats = true,
  customStyles = {}
}) => {
  const statColumns = [
    { key: 'fg_percentage', label: 'FG%' },
    { key: 'ft_percentage', label: 'FT%' },
    { key: 'three_pm', label: '3PM' },
    { key: 'rebounds', label: 'REB' },
    { key: 'assists', label: 'AST' },
    { key: 'steals', label: 'STL' },
    { key: 'blocks', label: 'BLK' },
    { key: 'turnovers', label: 'TO' },
    { key: 'points', label: 'PTS' },
  ];

  const zScoreMapping = {
    'fg_percentage': 'fg_impact',
    'ft_percentage': 'ft_impact',
    'three_pm': 'three_pm',
    'rebounds': 'rebounds',
    'assists': 'assists',
    'steals': 'steals',
    'blocks': 'blocks',
    'turnovers': 'turnovers',
    'points': 'points',
  };

  const formatValue = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(3);
    }
    return value;
  };

  const getZScoreColor = (value) => {
    const normalized = Math.max(-3, Math.min(3, value)) / 3;
    if (normalized > 0) {
      return `rgba(0, 255, 0, ${normalized * 0.3})`;
    } else {
      return `rgba(255, 0, 0, ${Math.abs(normalized) * 0.3})`;
    }
  };

  // Calculate team totals for z-scores
  const calculateTeamTotals = () => {
    const totals = {};
    const validPlayers = players.filter(p => p.name);

    statColumns.forEach(col => {
      const zScoreKey = zScoreMapping[col.key];
      const sum = validPlayers.reduce((acc, player) => {
        return acc + (player.z_scores[zScoreKey] || 0);
      }, 0);
      totals[zScoreKey] = sum;
    });

    return totals;
  };

  const teamTotals = showTeamTotals ? calculateTeamTotals() : null;
  const validPlayers = players.filter(player => player.name);

  return (
    <div className="table-container" style={customStyles.container}>
      {teamName && <h3>{teamName}</h3>}
      <table className="pure-table pure-table-horizontal" style={customStyles.table}>
        <thead>
          <tr>
            <th rowSpan="2">Name</th>
            {showStats && <th colSpan={statColumns.length}>Stats</th>}
            <th colSpan={statColumns.length}>Value (Z-Score)</th>
          </tr>
          <tr>
            {showStats && statColumns.map(col => (
              <th key={col.key}>{col.label}</th>
            ))}
            {statColumns.map(col => (
              <th key={`z-${col.key}`} className="z-score-header">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {validPlayers.map((player, idx) => (
            <tr key={`${player.name}-${idx}`}>
              <td>{player.name}</td>
              {/* Regular stats */}
              {showStats && statColumns.map(col => (
                <td key={col.key}>{player.stats[col.key]}</td>
              ))}
              {/* Z-scores */}
              {statColumns.map(col => {
                const zScore = player.z_scores[zScoreMapping[col.key]];
                return (
                  <td
                    key={`z-${col.key}`}
                    className="z-score"
                    style={{
                      backgroundColor: getZScoreColor(zScore)
                    }}
                  >
                    {formatValue(zScore)}
                  </td>
                );
              })}
            </tr>
          ))}

          {/* Team Totals Row */}
          {showTeamTotals && teamTotals && (
            <tr style={{ fontWeight: 'bold', borderTop: '2px solid #000' }}>
              <td style={{ fontWeight: 'bold' }}>TEAM TOTAL</td>
              {/* Empty cells for stats columns */}
              {showStats && statColumns.map(col => (
                <td key={`stat-total-${col.key}`}>—</td>
              ))}
              {/* Team z-score totals with color coding */}
              {statColumns.map(col => {
                const zScoreKey = zScoreMapping[col.key];
                const total = teamTotals[zScoreKey];
                return (
                  <td
                    key={`z-total-${col.key}`}
                    className="z-score team-total"
                    style={{
                      backgroundColor: getZScoreColor(total),
                      fontWeight: 'bold',
                      color: '#ffffff'
                    }}
                  >
                    {formatValue(total)}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PlayerStatsTable;
